import {
  Breadcrumbs,
  Button,
  Inline,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useCompany, useDocuments } from '@omnipresentgroup/domains';
import { DocumentList } from '@omnipresentgroup/features';
import { DocumentAction } from '@omnipresentgroup/types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { ADD_DOCUMENT_PATH } from '../../constants';
import { useManagerUser } from '../../hooks';

const COMPANY_DOCUMENTS_LABEL = 'Documents';
const ADD_DOCUMENT_BUTTON_LABEL = 'Add document';
const NO_DOCUMENTS_TITLE = 'No documents';
const NO_DOCUMENTS_MESSAGE = 'There are no documents for this company.';

export const CompanyDocuments = () => {
  const navigate = useNavigate();

  const { companyId } = useManagerUser();

  const { data: company } = useCompany({
    id: companyId,
    enabled: !!companyId,
  });

  const { data: documentsInfo } = useDocuments({
    companyId: companyId!,
    enabled: !!companyId,
  });

  const canAddDocuments = useMemo(
    () => documentsInfo?.actions.includes(DocumentAction.CREATE),
    [documentsInfo],
  );

  const PageHeader = () => (
    <Stack gap="0">
      <Inline justify="space-between" minH={40} align="center">
        <Inline gap="8" align="center">
          <Typography as="h2" hideParagraphSpacing size="24" weight="medium">
            {COMPANY_DOCUMENTS_LABEL}
          </Typography>
        </Inline>
        {canAddDocuments && (
          <Button onClick={() => navigate(ADD_DOCUMENT_PATH)}>
            {ADD_DOCUMENT_BUTTON_LABEL}
          </Button>
        )}
      </Inline>
      <Typography as="span" size="14" color="subtle">
        {company?.name}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'company-documents',
              title: COMPANY_DOCUMENTS_LABEL,
              active: true,
            },
          ]}
        />

        <PageHeader />
      </Stack>

      {companyId && (
        <DocumentList
          companyId={companyId}
          columnVisibility={{
            title: true,
            createdAt: true,
            actions: true,
          }}
          showHeader={false}
          emptyStateProps={{
            id: 'no-documents',
            title: NO_DOCUMENTS_TITLE,
            description: NO_DOCUMENTS_MESSAGE,
          }}
        />
      )}
    </>
  );
};
